import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toChallengesView, type ChallengeCompetitionFilter, type ChallengesView } from './challenges-model';

/**
 * The Challenge Hub's data layer: one read of `GET /product-insights/challenges`
 * per (cycle, competition). SEED's own figures through EMERGE's proxy (server
 * cache, nothing stored).
 *
 * - `cycleId: null` is "All editions": no cycle is sent at all.
 * - `competition: 'all'` is the default and is not sent.
 * - Previous data stays on screen while a new key loads (`refreshing`);
 *   `loading` is only true when there is nothing to show yet. Loading is
 *   derived from request keys, never set inside the effect.
 */

export type ChallengesRequestParams = Parameters<typeof api.productInsightsChallenges>[0];

interface ChallengesResult {
  key: string;
  data: ChallengesView | null;
  error: unknown | null;
}

export function useChallengesSummary(
  cycleId: number | null,
  competition: ChallengeCompetitionFilter,
  reloadToken: number,
): { data: ChallengesView | null; loading: boolean; error: unknown | null; refreshing: boolean } {
  const requestKey = `${cycleId ?? 'all'}|${competition}#${reloadToken}`;
  const [lastData, setLastData] = useState<ChallengesView | null>(null);
  const [result, setResult] = useState<ChallengesResult | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${cycleId ?? 'all'}|${competition}#${reloadToken}`;
    const params: ChallengesRequestParams = {
      ...(cycleId === null ? {} : { cycleId }),
      ...(competition === 'all' ? {} : { competition }),
    };
    api
      .productInsightsChallenges(params, signal)
      .then((raw: unknown) => {
        if (signal.aborted) return;
        const data = toChallengesView(raw);
        setLastData(data);
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key, data: null, error: err });
      });
    return () => controller.abort();
  }, [cycleId, competition, reloadToken]);

  const current = result !== null && result.key === requestKey ? result : null;
  const settled = current !== null;
  return {
    data: lastData,
    loading: lastData === null && !settled,
    error: current?.error ?? null,
    refreshing: lastData !== null && !settled,
  };
}
